// Problèmes trouvés par la validation : un chemin dans le document (pour que
// le Studio surligne l'élément) et un message en français.

import { type ZodErrorMap, ZodIssueCode, type ZodIssue } from 'zod';

export type JrafSeverity = 'error' | 'warning';

export interface JrafIssue {
  /** `scenes[2].steps[4].target` ; vide = tout le document */
  path: string;
  code: string;
  message: string;
  severity: JrafSeverity;
}

export type PathPart = string | number;

export function formatPath(parts: readonly PathPart[]): string {
  let out = '';
  for (const p of parts) {
    if (typeof p === 'number') out += `[${p}]`;
    else out += out ? `.${p}` : p;
  }
  return out;
}

export function issue(
  path: readonly PathPart[],
  code: string,
  message: string,
  severity: JrafSeverity = 'error',
): JrafIssue {
  return { path: formatPath(path), code, message, severity };
}

function valueAt(root: unknown, path: readonly PathPart[]): unknown {
  let cur: unknown = root;
  for (const p of path) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string | number, unknown>)[p];
  }
  return cur;
}

const TYPE_FR: Record<string, string> = {
  string: 'un texte',
  number: 'un nombre',
  boolean: 'vrai ou faux',
  object: 'un objet',
  array: 'une liste',
  undefined: 'rien',
  null: 'null',
  integer: 'un entier',
};

/** Messages français des erreurs de structure (Zod). */
export const frenchErrorMap: ZodErrorMap = (iss, ctx) => {
  switch (iss.code) {
    case ZodIssueCode.invalid_type:
      if (iss.received === 'undefined') return { message: 'champ obligatoire manquant' };
      return {
        message: `${TYPE_FR[iss.expected] ?? iss.expected} attendu, ${TYPE_FR[iss.received] ?? iss.received} reçu`,
      };
    case ZodIssueCode.invalid_literal:
      return { message: `valeur attendue : ${JSON.stringify(iss.expected)}` };
    case ZodIssueCode.unrecognized_keys:
      return {
        message: `propriété${iss.keys.length > 1 ? 's' : ''} non autorisée${iss.keys.length > 1 ? 's' : ''} : ${iss.keys.join(', ')}`,
      };
    case ZodIssueCode.invalid_enum_value:
      return {
        message: `valeur « ${String(iss.received)} » non autorisée (attendu : ${iss.options.join(', ')})`,
      };
    case ZodIssueCode.invalid_union:
      return { message: 'valeur invalide (nombre, expression ou forme attendue)' };
    case ZodIssueCode.too_small:
      if (iss.type === 'array') return { message: `au moins ${iss.minimum} élément(s)` };
      if (iss.type === 'string') return { message: `au moins ${iss.minimum} caractère(s)` };
      return { message: `doit valoir au moins ${iss.minimum}` };
    case ZodIssueCode.too_big:
      if (iss.type === 'array') return { message: `au plus ${iss.maximum} élément(s)` };
      if (iss.type === 'string') return { message: `au plus ${iss.maximum} caractères` };
      return { message: `doit valoir au plus ${iss.maximum}` };
    case ZodIssueCode.invalid_string:
      return { message: 'format invalide' };
    case ZodIssueCode.not_finite:
      return { message: 'nombre fini attendu' };
    default:
      return { message: ctx.defaultError };
  }
};

/** Convertit une erreur de structure Zod en problème JRAF. */
export function fromZodIssue(z: ZodIssue, input: unknown): JrafIssue {
  if (z.code === ZodIssueCode.invalid_union_discriminator) {
    const field = z.path[z.path.length - 1];
    const got = valueAt(input, z.path);
    const kind = field === 'action' ? 'action' : field === 'type' ? 'type d’objet' : String(field);
    return issue(
      z.path,
      field === 'action'
        ? 'unknown_action'
        : field === 'type'
          ? 'unknown_type'
          : 'invalid_discriminator',
      got === undefined
        ? `${kind} manquant`
        : `${kind} « ${String(got)} » inconnu (catalogue : ${z.options.map(String).join(', ')})`,
    );
  }
  return issue(z.path, `schema.${z.code}`, z.message);
}
